/**
 * SprintReportChart — grouped bar chart showing committed, completed,
 * and carried-over metrics per sprint swimlane.
 *
 * Uses the standard ChartProps interface for compatibility with ChartRenderer,
 * but the data is encoded as: dimensionValue = "Sprint|metric" and
 * measureValue = count. The chart decodes and renders as grouped bars.
 *
 * Data encoding from the BFF query executor:
 *   { dimensionValue: "Sprint 1|committed", measureValue: 12 }
 *   { dimensionValue: "Sprint 1|completed", measureValue: 10 }
 *   { dimensionValue: "Sprint 1|carriedOver", measureValue: 2 }
 *
 * Requirements: 4.1–4.7
 */

import React, { useMemo } from 'react';
import { ChartProps } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SprintGroup {
  readonly label: string;
  readonly committed: number;
  readonly completed: number;
  readonly carriedOver: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLORS = {
  committed: '#3b82f6',   // blue-500
  completed: '#22c55e',   // green-500
  carriedOver: '#f59e0b', // amber-500
  average: '#6366f1',     // indigo-500
};

const BAR_GROUP_GAP = 8;
const BAR_WIDTH = 20;
const CHART_HEIGHT = 280;
const CHART_PADDING_LEFT = 50;
const CHART_PADDING_BOTTOM = 60;
const CHART_PADDING_TOP = 20;
const CHART_PADDING_RIGHT = 20;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SprintReportChart({ data, onSegmentClick }: ChartProps): React.ReactElement {
  // Decode grouped data from the flat dimensionValue|metric format
  const sprints = useMemo(() => parseSprints(data), [data]);

  // Compute trailing 3-sprint average for completed
  const trailingAvg = useMemo(() => {
    return sprints.map((_, i) => {
      if (i < 2) return null;
      const window = sprints.slice(i - 2, i + 1);
      return window.reduce((sum, s) => sum + s.completed, 0) / window.length;
    });
  }, [sprints]);

  if (sprints.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        No sprints configured. Set start and end dates on swimlanes to enable sprint tracking.
      </div>
    );
  }

  // Calculate chart dimensions
  const groupWidth = BAR_WIDTH * 3 + BAR_GROUP_GAP * 2;
  const totalGroupsWidth = sprints.length * (groupWidth + BAR_GROUP_GAP * 2);
  const chartWidth = Math.max(400, totalGroupsWidth + CHART_PADDING_LEFT + CHART_PADDING_RIGHT);
  const maxValue = Math.max(1, ...sprints.flatMap((s) => [s.committed, s.completed, s.carriedOver]));
  const yScale = (CHART_HEIGHT - CHART_PADDING_TOP - CHART_PADDING_BOTTOM) / maxValue;

  return (
    <div className="w-full overflow-x-auto" role="figure" aria-label="Sprint report chart">
      <svg
        width={chartWidth}
        height={CHART_HEIGHT}
        viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
        className="block"
      >
        {/* Y-axis gridlines */}
        {Array.from({ length: 5 }, (_, i) => {
          const value = Math.round((maxValue / 4) * i);
          const y = CHART_HEIGHT - CHART_PADDING_BOTTOM - value * yScale;
          return (
            <g key={i}>
              <line
                x1={CHART_PADDING_LEFT}
                x2={chartWidth - CHART_PADDING_RIGHT}
                y1={y}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.1}
              />
              <text
                x={CHART_PADDING_LEFT - 8}
                y={y + 4}
                textAnchor="end"
                className="text-[10px] fill-muted-foreground"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {sprints.map((sprint, i) => {
          const groupX = CHART_PADDING_LEFT + i * (groupWidth + BAR_GROUP_GAP * 2) + BAR_GROUP_GAP;
          const baseY = CHART_HEIGHT - CHART_PADDING_BOTTOM;

          return (
            <g key={sprint.label}>
              {/* Committed bar */}
              <rect
                x={groupX}
                y={baseY - sprint.committed * yScale}
                width={BAR_WIDTH}
                height={sprint.committed * yScale}
                fill={COLORS.committed}
                rx={2}
                className="cursor-pointer hover:opacity-80"
                onClick={() => onSegmentClick?.(`${sprint.label}|committed`)}
              >
                <title>{sprint.label} — Committed: {sprint.committed}</title>
              </rect>

              {/* Completed bar */}
              <rect
                x={groupX + BAR_WIDTH + BAR_GROUP_GAP}
                y={baseY - sprint.completed * yScale}
                width={BAR_WIDTH}
                height={sprint.completed * yScale}
                fill={COLORS.completed}
                rx={2}
                className="cursor-pointer hover:opacity-80"
                onClick={() => onSegmentClick?.(`${sprint.label}|completed`)}
              >
                <title>{sprint.label} — Completed: {sprint.completed}</title>
              </rect>

              {/* Carried Over bar */}
              <rect
                x={groupX + (BAR_WIDTH + BAR_GROUP_GAP) * 2}
                y={baseY - sprint.carriedOver * yScale}
                width={BAR_WIDTH}
                height={sprint.carriedOver * yScale}
                fill={COLORS.carriedOver}
                rx={2}
                className="cursor-pointer hover:opacity-80"
                onClick={() => onSegmentClick?.(`${sprint.label}|carriedOver`)}
              >
                <title>{sprint.label} — Carried Over: {sprint.carriedOver}</title>
              </rect>

              {/* Sprint label */}
              <text
                x={groupX + groupWidth / 2}
                y={CHART_HEIGHT - CHART_PADDING_BOTTOM + 16}
                textAnchor="middle"
                className="text-[11px] fill-muted-foreground"
              >
                {sprint.label}
              </text>
            </g>
          );
        })}

        {/* Trailing average line */}
        {trailingAvg.filter(Boolean).length > 1 && (
          <path
            d={buildAveragePath(sprints, trailingAvg, groupWidth, yScale)}
            fill="none"
            stroke={COLORS.average}
            strokeWidth={2}
            strokeDasharray="4 2"
            opacity={0.8}
          />
        )}
      </svg>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.committed }} />
          Committed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.completed }} />
          Completed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.carriedOver }} />
          Carried Over
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 border-t-2 border-dashed" style={{ borderColor: COLORS.average }} />
          3-Sprint Avg
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseSprints(data: ChartProps['data']): SprintGroup[] {
  const map = new Map<string, SprintGroup>();

  for (const d of data) {
    const [label, metric] = d.dimensionValue.split('|');
    if (!label || !metric) continue;

    const existing = map.get(label) ?? { label, committed: 0, completed: 0, carriedOver: 0 };

    switch (metric) {
      case 'committed':
        map.set(label, { ...existing, committed: d.measureValue });
        break;
      case 'completed':
        map.set(label, { ...existing, completed: d.measureValue });
        break;
      case 'carriedOver':
        map.set(label, { ...existing, carriedOver: d.measureValue });
        break;
    }
  }

  // Return in insertion order (already sorted by API — most recent first, reversed for chart L→R)
  return Array.from(map.values()).reverse();
}

function buildAveragePath(
  sprints: SprintGroup[],
  trailingAvg: (number | null)[],
  groupWidth: number,
  yScale: number,
): string {
  const baseY = CHART_HEIGHT - CHART_PADDING_BOTTOM;
  const points: string[] = [];

  for (let i = 0; i < sprints.length; i++) {
    const avg = trailingAvg[i];
    if (avg === null) continue;
    const x = CHART_PADDING_LEFT + i * (groupWidth + BAR_GROUP_GAP * 2) + BAR_GROUP_GAP + groupWidth / 2;
    const y = baseY - avg * yScale;
    points.push(`${points.length === 0 ? 'M' : 'L'} ${x} ${y}`);
  }

  return points.join(' ');
}
